import { useState, useEffect } from 'react';
import { doseLogsAPI } from '../api/api';
import { toast } from 'sonner';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

const HistoryScreen = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const slotNames = {
    before_breakfast: 'Before Breakfast',
    breakfast: 'After Breakfast',
    before_lunch: 'Before Lunch',
    lunch: 'After Lunch',
    before_dinner: 'Before Dinner',
    dinner: 'After Dinner',
    bed: 'Before Sleep',
    prn: 'As Needed',
  };

  const skipReasonLabels = {
    forgot: 'Forgot',
    felt_unwell: 'Felt Unwell',
    not_at_home: 'Not at Home',
    other: 'Other',
  };

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const data = await doseLogsAPI.getHistory();
      setHistory(data);
    } catch (error) {
      toast.error('Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const groupByDate = (logs) => {
    const groups = {};
    logs.forEach((item) => {
      const date = item.log.scheduled_date;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(item);
    });
    return groups;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'taken':
        return <CheckCircle className="h-8 w-8 text-green-600" />;
      case 'skipped':
        return <AlertCircle className="h-8 w-8 text-yellow-600" />;
      case 'missed':
        return <XCircle className="h-8 w-8 text-red-600" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="history-screen">
        <div className="text-2xl font-medium text-gray-600">Loading...</div>
      </div>
    );
  }

  const groupedHistory = groupByDate(history);

  return (
    <div className="screen-container bg-gradient-to-b from-green-50 to-white min-h-screen" data-testid="history-screen">
      <div className="p-6 space-y-6">
        <h1 className="text-4xl font-bold text-gray-900">History</h1>

        {history.length === 0 ? (
          <div className="text-center py-16" data-testid="no-history">
            <div className="text-6xl mb-4">📅</div>
            <p className="text-2xl font-semibold text-gray-600 mb-2">
              No history yet
            </p>
            <p className="text-lg text-gray-500">
              Your medication history will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.keys(groupedHistory).map((date) => (
              <div key={date}>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {formatDate(date)}
                </h2>
                <div className="space-y-3">
                  {groupedHistory[date].map((item, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl p-5 shadow-sm border-2 border-gray-200"
                      data-testid={`history-item-${index}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          {getStatusIcon(item.log.status)}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-1">
                            {item.medication_name}
                          </h3>
                          <p className="text-base text-gray-600 mb-1">
                            {slotNames[item.log.routine_slot]}
                          </p>
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                                item.log.status === 'taken'
                                  ? 'bg-green-100 text-green-800'
                                  : item.log.status === 'skipped'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {item.log.status === 'taken'
                                ? 'Taken'
                                : item.log.status === 'skipped'
                                ? 'Skipped'
                                : 'Missed'}
                            </span>
                            {item.log.skip_reason && (
                              <span className="text-sm text-gray-500">
                                ({skipReasonLabels[item.log.skip_reason]})
                              </span>
                            )}
                          </div>
                        </div>
                        {item.photo_url && (
                          <img
                            src={item.photo_url}
                            alt={item.medication_name}
                            className="w-16 h-16 rounded-lg object-cover border-2 border-gray-200"
                          />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryScreen;